// frontend/src/components/chat/patient-panel/ProblemsList.tsx
import { FC, useCallback } from 'react'
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { SemanticExtraction } from '../../../contexts/ChatContext'
import { ProblemCard } from './ProblemCard'
import { useProblemRanking } from './hooks/useProblemRanking'
import { EnhancedProblem } from './types'

interface ProblemsListProps {
  extractions: SemanticExtraction[]
  patientId: string
  onProblemClick?: (problem: EnhancedProblem) => void
}

const SortableProblemCard: FC<{
  problem: EnhancedProblem
  onPin: () => void
  onClick?: () => void
}> = ({ problem, onPin, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: problem.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ProblemCard
        problem={problem}
        onPin={onPin}
        onClick={onClick}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

export const ProblemsList: FC<ProblemsListProps> = ({
  extractions,
  patientId,
  onProblemClick,
}) => {
  const { problems, handleReorder, togglePin } = useProblemRanking(extractions, patientId)

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = problems.findIndex(p => p.id === active.id)
    const newIndex = problems.findIndex(p => p.id === over.id)

    const newOrder = [...problems]
    const [removed] = newOrder.splice(oldIndex, 1)
    newOrder.splice(newIndex, 0, removed)
    handleReorder(newOrder)
  }, [problems, handleReorder])

  if (problems.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No problems documented
      </div>
    )
  }

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={problems.map(p => p.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1 p-2">
          {problems.map(problem => (
            <SortableProblemCard
              key={problem.id}
              problem={problem}
              onPin={() => togglePin(problem.id)}
              onClick={onProblemClick ? () => onProblemClick(problem) : undefined}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
